/**
 * The widget shell.
 *
 * Desktop: a non-modal Liquid Glass card above the launcher. No focus trap and
 * no scrim — the page behind it stays usable, which is the point of a widget,
 * and the §M glass treatment is what makes that companion layer read as one.
 * Mobile (<768): a full-height sheet with a scrim, `aria-modal`, and a body
 * scroll lock, because it covers the app. The sheet stays SOLID `bg-card` —
 * it sits over its own scrim, so a blur has nothing to reveal, and a
 * backdrop-filter on a full-height scrolling surface janks on mid-range
 * Android.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { History, Maximize2, SquarePen, X } from 'lucide-react';

import { LAUNCHER_SIZE_PX } from './AlfieLauncher';

import HearthstoneIcon from '@/components/icons/HearthstoneIcon';
import { useIsMobile } from '@/hooks/use-mobile';

interface AlfieWidgetPanelProps {
  /** Subtitle under "Alfie" — the current thread's title, or "New chat". */
  subtitle: string;
  /** The launcher's bottom edge in px (desktop); the card sits 16px above it. */
  launcherBottomPx: number;
  onClose: () => void;
  onOpenHistory: () => void;
  onNewChat: () => void;
  onExpand: () => void;
  /** Hidden while the history view owns the header. */
  showHeader: boolean;
  /** Finance mode (the Xero screens): a "Finance" pill beside the name. */
  finance?: boolean;
  children: ReactNode;
}

/** Gap between the launcher's top edge and the desktop card. */
const PANEL_GAP_PX = 16;
/** Breathing room kept above the desktop card at short viewports. */
const PANEL_TOP_MARGIN_PX = 28;

export function AlfieWidgetPanel({
  finance = false,
  subtitle,
  launcherBottomPx,
  onClose,
  onOpenHistory,
  onNewChat,
  onExpand,
  showHeader,
  children,
}: AlfieWidgetPanelProps) {
  const isMobile = useIsMobile();
  const [entered, setEntered] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // One frame in the "from" state, then transition to the "to" state.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  /**
   * Escape closes the widget. The mobile sheet is modal, so any Escape belongs
   * to it. The desktop card is NOT modal — the page behind it stays live — so
   * it only claims an Escape that was aimed at it: one nobody has already
   * handled (`defaultPrevented`), raised from inside the panel or from the
   * bare document. Without that, Escape dismissing a page-level menu or
   * clearing a search field tore the widget down with it.
   */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isMobile) {
        onClose();
        return;
      }
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement | null;
      const ownsEvent = target === document.body || !!target?.closest?.('[data-alfie-panel]');
      if (!ownsEvent) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, isMobile]);

  // The mobile sheet covers the app, so the page behind it must not scroll.
  useEffect(() => {
    if (!isMobile) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobile]);

  const hit = isMobile ? 'h-11 w-11' : 'h-8 w-8';
  const iconSize = isMobile ? 'h-5 w-5' : 'h-[18px] w-[18px]';

  const headerButton = `flex shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground ${hit}`;

  const panel = (
    <div
      ref={panelRef}
      data-alfie-panel
      role={isMobile ? 'dialog' : undefined}
      aria-modal={isMobile ? true : undefined}
      aria-label="Alfie"
      style={
        isMobile
          ? { top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }
          : {
              // Default launcher (bottom 20) puts the card at bottom 92 with a
              // 100dvh-120 cap; a lifted launcher moves both by the same amount.
              bottom: `${launcherBottomPx + LAUNCHER_SIZE_PX + PANEL_GAP_PX}px`,
              height: `min(680px, calc(100dvh - ${
                launcherBottomPx + LAUNCHER_SIZE_PX + PANEL_GAP_PX + PANEL_TOP_MARGIN_PX
              }px))`,
              // Safari still ships the prefix only, so both spellings go out.
              backdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
              WebkitBackdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
            }
      }
      className={[
        'fixed flex flex-col overflow-hidden',
        isMobile
          ? // The dark hairline separates a #252422 sheet from the dimmed page
            // behind it; in light mode the white sheet separates on its own.
            // z-[54] over its z-[53] scrim: the sheet is modal and covers the app,
            // including any dialog at z-50. See LAYERING in AlfieLauncher.
            'z-[54] bg-card inset-x-0 bottom-0 rounded-t-[24px] dark:border-t dark:border-border'
          : // z-[54], raised 6 Oct from z-[45]. The old rule was that a surface
            // the user opens from the page always paints OVER the widget. That
            // inverted once Alfie had to be reachable from inside a dialog: the
            // launcher at z-[52] would open this panel behind the very dialog
            // the question was about. Now the widget tops the dialog layer and
            // stops below the mobile nav, alert-dialog and the z-[100] modals —
            // see LAYERING in AlfieLauncher.
            'z-[54] right-5 w-[420px] rounded-[20px] border border-[hsl(var(--glass-border))] bg-[hsl(var(--glass-bg))]',
        'shadow-[0_4px_16px_rgba(0,0,0,0.06),0_16px_48px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.3),0_16px_48px_rgba(0,0,0,0.3)]',
        'origin-bottom-right transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
        entered ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
      ].join(' ')}
    >
      {showHeader && (
        <div className="flex h-[60px] shrink-0 items-center gap-2.5 border-b border-border pl-4 pr-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15">
            <HearthstoneIcon size={18} variant="brand" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-px">
            <span
              className={`font-semibold leading-[18px] text-foreground ${
                isMobile ? 'text-[15px]' : 'text-sm'
              }`}
            >
              Alfie
              {finance && (
                // The finance pillar colour: Alfie answers about the books only in this mode.
                <span className="ml-1.5 inline-flex h-[18px] items-center rounded-full bg-pillar-finance/15 px-2 align-middle text-[11px] font-semibold leading-none text-pillar-finance">
                  Finance
                </span>
              )}
            </span>
            <span className="truncate text-xs leading-4 text-muted-foreground">{subtitle}</span>
          </div>
          <button type="button" onClick={onOpenHistory} aria-label="Chat history" className={headerButton}>
            <History className={iconSize} strokeWidth={1.75} />
          </button>
          <button type="button" onClick={onNewChat} aria-label="New chat" className={headerButton}>
            <SquarePen className={iconSize} strokeWidth={1.75} />
          </button>
          {/* Mobile keeps three actions only: with 44px hit targets a fourth
              squeezes the title block to nothing. The sheet is already
              full-height, so "open in /alfie" buys almost no room there. */}
          {!isMobile && (
            <button
              type="button"
              onClick={onExpand}
              aria-label="Open in full Alfie"
              className={headerButton}
            >
              <Maximize2 className={iconSize} strokeWidth={1.75} />
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="Close Alfie" className={headerButton}>
            <X className={iconSize} strokeWidth={1.75} />
          </button>
        </div>
      )}
      {children}
    </div>
  );

  if (!isMobile) return panel;

  return (
    <>
      <div
        aria-hidden
        // pointerdown, never click: a tap that drifts a few pixels before it
        // lifts never fires a click, so the sheet ignored it. `preventDefault`
        // is what makes that safe — it suppresses the compatibility mouse
        // events, so the tap cannot fall through onto whatever page control
        // the scrim was covering once the sheet unmounts.
        onPointerDown={(e) => {
          e.preventDefault();
          onClose();
        }}
        // The scrim is dark in BOTH themes. `bg-foreground/45` alone inverts to
        // a near-white wash in dark mode, which fogs the page instead of
        // dimming it — the design calls for black 55% there.
        className={`fixed inset-0 z-[53] bg-foreground/45 transition-opacity duration-200 dark:bg-black/55 motion-reduce:transition-none ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {panel}
    </>
  );
}

export default AlfieWidgetPanel;
